import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import api from '../services/api';
import Text from '../components/shared/Text';
import StockAdjustmentModal from '../components/products/StockAdjustmentModal';

function ProductShow() {
    const { id } = useParams();
    const [product, setProduct] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination state
    const [units, setUnits] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUnits, setTotalUnits] = useState(0);

    const [showAddUnitModal, setShowAddUnitModal] = useState(false);
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [adjustments, setAdjustments] = useState<any[]>([]);
    const [stockBatches, setStockBatches] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'details' | 'history' | 'units' | 'batches'>('details');
    const [taxRates, setTaxRates] = useState<any[]>([]);
    const [updatingTax, setUpdatingTax] = useState(false);

    const getEmptyRow = () => {
        const row: Record<string, string> = { batch_number: '' };
        const types = product?.tracking_types?.length ? product.tracking_types : ['Serial Number'];
        types.forEach((t: string) => row[t] = '');
        return row;
    };

    const [unitRows, setUnitRows] = useState<Record<string, string>[]>([{}]);

    useEffect(() => {
        if (product && showAddUnitModal && unitRows.length === 1 && Object.keys(unitRows[0]).length === 0) {
            setUnitRows([getEmptyRow()]);
        }
    }, [product, showAddUnitModal]);

    const fetchUnits = async (page: number) => {
        try {
            const response = await api.get(`/products/${id}/units?page=${page}`);
            if (response.success || response.units) {
                setUnits(response.units || response.data?.units || []);
                setTotalUnits(response.meta?.total_count || response.data?.meta?.total_count || 0);
                setTotalPages(response.meta?.total_pages || response.data?.meta?.total_pages || 1);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    };

    const fetchAdjustments = async () => {
        try {
            const resp = await api.get(`/products/${id}/stock_adjustments`);
            // API now returns a merged flat array of movements + adjustments
            const data = resp.data || [];
            setAdjustments(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to fetch adjustments", e);
        }
    };

    const fetchStockBatches = async () => {
        try {
            const resp = await api.get(`/products/${id}`);
            const productData = resp.data?.product || resp.product || resp.data || resp;
            if (productData?.batches) {
                setStockBatches(productData.batches);
            }
        } catch (e) {
            console.error("Failed to fetch stock batches", e);
        }
    };

    const loadProduct = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/products/${id}`);
            const productData = response.data?.product || response.product || response.data || response;
            if (productData && productData.id) {
                setProduct(productData);

                // If product is serialized, fetch units
                if (productData.serialized) {
                    await fetchUnits(1);
                }
                await fetchAdjustments();
                await fetchStockBatches();
            } else {
                setError(response.message || 'Product synchronization failure.');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load product');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadProduct();
            fetchTaxRates();
        }
    }, [id]);

    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || response.data?.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    const handleQuickTaxUpdate = async (taxRateId: string) => {
        if (!taxRateId) return;
        try {
            setUpdatingTax(true);
            const response = await api.put(`/products/${id}`, {
                product: { tax_rate_id: taxRateId }
            });
            if (response.data || response.success) {
                loadProduct();
            }
        } catch (error) {
            console.error('Tax update failed:', error);
        } finally {
            setUpdatingTax(false);
        }
    };

    const addEmptyRow = () => {
        setUnitRows([...unitRows, getEmptyRow()]);
    };

    const removeRow = (index: number) => {
        const newRows = [...unitRows];
        newRows.splice(index, 1);
        setUnitRows(newRows);
    };

    const updateRow = (index: number, field: string, value: string) => {
        const newRows = [...unitRows];
        newRows[index] = { ...newRows[index], [field]: value };
        setUnitRows(newRows);
    };

    const handlePaste = (e: React.ClipboardEvent, index: number) => {
        e.preventDefault();
        const clipboardData = e.clipboardData.getData('text/plain');
        if (!clipboardData) return;

        const lines = clipboardData.split(/\r\n|\n|\r/);
        const newRows = [...unitRows];
        const types = product?.tracking_types?.length ? product.tracking_types : ['Serial Number'];

        let currentRowIdx = index;

        lines.forEach((line) => {
            if (!line.trim()) return;
            const columns = line.split(/\t|,/); // Split by tab or comma

            const rowData: Record<string, string> = { batch_number: '' };
            types.forEach((t: string, i: number) => {
                rowData[t] = columns[i]?.trim() || '';
            });
            rowData.batch_number = columns[types.length]?.trim() || '';

            if (currentRowIdx < newRows.length) {
                newRows[currentRowIdx] = { ...newRows[currentRowIdx], ...rowData };
            } else {
                newRows.push({ ...getEmptyRow(), ...rowData });
            }
            currentRowIdx++;
        });

        setUnitRows(newRows);
    };

    const handleSaveUnits = async () => {
        const firstType = (product?.tracking_types?.length ? product.tracking_types : ['Serial Number'])[0];
        const validRows = unitRows.filter(r => r[firstType]?.trim());
        if (validRows.length === 0) return;

        try {
            const identifiers = validRows.map(row => {
                const data = { ...row };
                return data;
            });

            const payload = {
                identifiers
            };

            const response = await api.post(`/products/${id}/units`, payload);

            if (response.units || response.success) {
                setUnitRows([getEmptyRow()]);
                setShowAddUnitModal(false);
                fetchUnits(1);
                setCurrentPage(1);
                loadProduct();
            }
        } catch (error) {
            console.error('Error adding units:', error);
        }
    };

    const handleDownloadQR = () => {
        const svg = document.getElementById("product-qr-code") as any;
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d") as any;
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const pngFile = canvas.toDataURL("image/png");

            const downloadLink = document.createElement("a");
            downloadLink.download = `QR_${product.slug || product.sku || product.id}.png`;
            downloadLink.href = pngFile;
            downloadLink.click();
        };

        img.src = "data:image/svg+xml;base64," + btoa(svgData);
    };

    if (loading && !product) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <Text color="slate" weight="medium">Loading product details...</Text>
            </div>
        );
    }

    if (error) return <div className="mt-8 p-6 bg-rose-50 text-rose-600 rounded-2xl text-sm font-medium border border-rose-100">{error}</div>;
    if (!product) return <div className="mt-8 p-6 bg-slate-50 text-slate-500 rounded-2xl text-sm font-medium border border-slate-200">Product not found.</div>;

    const isLowStock = product.stock_quantity <= (product.low_stock_threshold || 5);
    const publicUrl = `${window.location.origin}/p/${product.slug || product.id}`;

    return (
        <div className="mt-6 px-1 max-w-7xl mx-auto">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <Link to="/products" className="w-10 h-10 bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-100 hover:shadow-lg rounded-xl flex items-center justify-center transition-all duration-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Text variant="h2" color="primary">{product.name}</Text>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${product.active ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                {product.active ? 'Active' : 'Disabled'}
                            </span>
                            {product.discontinued && (
                                <span className="px-2 py-0.5 bg-rose-50 text-rose-600 border border-rose-100 rounded-full text-[10px] font-bold">
                                    Discontinued
                                </span>
                            )}
                        </div>
                        <Text variant="small" color="slate" className="mt-0.5 uppercase tracking-wide">SKU: {product.sku || 'N/A'}</Text>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {product.serialized && (
                        <button
                            onClick={() => setShowAddUnitModal(true)}
                            className="px-4 py-2 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg font-semibold hover:bg-indigo-100 transition-colors text-sm"
                        >
                            + Add Units
                        </button>
                    )}
                    <button
                        onClick={() => setShowAdjustmentModal(true)}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors text-sm"
                    >
                        Adjust Stock
                    </button>
                    <Link
                        to={`/products/${product.id}/edit`}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors text-sm"
                    >
                        Edit
                    </Link>
                    <Link
                        to={`/products/${product.id}/print_labels`}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors text-sm shadow-sm shadow-indigo-200"
                    >
                        Print Labels
                    </Link>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex gap-6 mb-8 border-b border-slate-200 overflow-x-auto">
                <button 
                  onClick={() => setActiveTab('details')}
                  className={`pb-4 px-2 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === 'details' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Details
                  {activeTab === 'details' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"></div>}
                </button>
                {product.serialized && (
                    <button 
                      onClick={() => setActiveTab('units')}
                      className={`pb-4 px-2 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === 'units' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Units ({totalUnits})
                      {activeTab === 'units' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"></div>}
                    </button>
                )}
                {(product.batches?.length > 0) && (
                    <button 
                      onClick={() => setActiveTab('batches')}
                      className={`pb-4 px-2 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === 'batches' ? 'text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      📦 Stock Batches ({product.batches?.length || 0})
                      {activeTab === 'batches' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-full"></div>}
                    </button>
                )}
                <button 
                  onClick={() => setActiveTab('history')}
                  className={`pb-4 px-2 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === 'history' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Stock History
                  {activeTab === 'history' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"></div>}
                </button>
            </div>

            {activeTab === 'details' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Information Grid */}
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <div className="flex items-center gap-2 mb-6">
                                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <Text variant="h3">General Information</Text>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                                <div>
                                    <Text variant="label" color="slate" className="mb-1">Category</Text>
                                    <Text weight="semibold" color="primary">{product.category_name || 'Uncategorized'}</Text>
                                </div>
                                <div>
                                    <Text variant="label" color="slate" className="mb-1">Default Batch</Text>
                                    <Text weight="semibold" color="primary">{product.batch_number || 'N/A'}</Text>
                                </div>
                                <div>
                                    <Text variant="label" color="slate" className="mb-1">HSN Code</Text>
                                    <Text weight="semibold" color="primary">{product.hsn_code || 'N/A'}</Text>
                                </div>
                                <div>
                                    <Text variant="label" color="slate" className="mb-1">Base Unit</Text>
                                    <Text weight="semibold" color="primary">{product.unit || 'Nos'}</Text>
                                </div>
                                <div>
                                    <Text variant="label" color="slate" className="mb-1">Tracking</Text>
                                    <Text weight="semibold" color="primary">{product.serialized ? 'Serialized' : 'Standard'}</Text>
                                </div>
                                <div>
                                    <Text variant="label" color="slate" className="mb-1">Installation</Text>
                                    <Text weight="semibold" color="primary">{product.requires_installation ? 'Required' : 'Not Required'}</Text>
                                </div>
                            </div>
                            
                            {product.description && (
                                <div className="mt-8 pt-6 border-t border-slate-100">
                                    <Text variant="label" color="slate" className="mb-2">Description</Text>
                                    <Text variant="body" color="secondary" className="leading-relaxed">{product.description}</Text>
                                </div>
                            )}
                        </div>

                        {/* Pricing & Commercials */}
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <div className="flex items-center gap-2 mb-6">
                                <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <Text variant="h3">Commercial Intelligence</Text>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                                    <Text variant="label" color="slate" className="mb-2">Purchase</Text>
                                    <Text variant="h3" color="primary">₹{product.purchase_price?.toLocaleString()}</Text>
                                </div>
                                <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-100">
                                    <Text variant="label" color="indigo" className="mb-2">MRP</Text>
                                    <Text variant="h3" color="indigo">₹{product.mrp?.toLocaleString()}</Text>
                                </div>
                                <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100 group relative overflow-hidden">
                                    <div className="flex justify-between items-start mb-2">
                                        <Text variant="label" color="emerald">Tax Rate</Text>
                                        <select 
                                            value={product.tax_rate_id || ''}
                                            onChange={(e) => handleQuickTaxUpdate(e.target.value)}
                                            disabled={updatingTax}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            title="Quick Update Tax Rate"
                                        >
                                            <option value="">Select Tax Rate</option>
                                            {taxRates.map(tr => (
                                                <option key={tr.id} value={tr.id}>{tr.name} ({tr.percentage}%)</option>
                                            ))}
                                        </select>
                                        <svg className={`w-3.5 h-3.5 text-emerald-400 group-hover:text-emerald-600 transition-colors ${updatingTax ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </div>
                                    <Text variant="h3" color="emerald">
                                        {product.tax_rate_name || (product.tax_rate_percentage !== undefined ? `${product.tax_rate_percentage}%` : `${product.tax_rate || 0}%`)}
                                    </Text>
                                </div>
                                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                                    <Text variant="label" color="slate" className="mb-2">Inclusive</Text>
                                    <Text variant="h3" color="primary">{product.tax_inclusive ? 'Yes' : 'No'}</Text>
                                </div>
                            </div>
                        </div>

                        {/* Custom Fields */}
                        {product.custom_fields && product.custom_fields.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <div className="flex items-center gap-2 mb-6">
                                    <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                    <Text variant="h3">Specifications</Text>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                    {product.custom_fields.map((field: any, idx: number) => (
                                        <div key={idx} className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                            <Text variant="label" color="slate" className="mb-1">{field.label}</Text>
                                            <Text weight="semibold" color="primary">{field.value || '—'}</Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Batch Information — only on details tab, simplified preview */}
                        {product.batches && product.batches.length > 0 && (
                            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3">
                                <span className="text-2xl">📦</span>
                                <div>
                                    <p className="font-bold text-amber-800 text-sm">{product.batches.length} Stock Batch{product.batches.length > 1 ? 'es' : ''} tracked</p>
                                    <button onClick={() => setActiveTab('batches')} className="text-amber-600 text-xs font-semibold hover:underline mt-0.5">
                                        View all batches →
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Area */}
                    <div className="space-y-6">
                        {/* Summary Widget */}
                        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
                            <Text variant="label" color="slate" className="mb-4">Stock Overview</Text>
                            <div className="space-y-6">
                                <div>
                                    <Text variant="stat" color="primary">{product.stock_quantity || 0}</Text>
                                    <Text variant="desc" className="mt-1">Units currently in stock</Text>
                                </div>
                                {isLowStock && (
                                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-3">
                                        <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                        <div>
                                            <Text variant="label" color="amber" className="leading-tight">Low Stock Alert</Text>
                                            <Text variant="small" color="amber" className="mt-1">Threshold: {product.low_stock_threshold || 5} units</Text>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick QR Widget */}
                        <div className="bg-white rounded-xl border border-slate-200 p-8 flex flex-col items-center text-center shadow-sm">
                            <div className="p-5 bg-slate-50 rounded-3xl mb-6 ring-1 ring-slate-100">
                                <QRCode id="product-qr-code" value={publicUrl} size={140} level="H" />
                            </div>
                            <Text variant="h3" color="primary">Product Identity</Text>
                            <Text variant="desc" className="mt-1 mb-6">Scan for public details</Text>
                            <div className="w-full grid grid-cols-2 gap-3">
                                <button onClick={handleDownloadQR} className="px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors">Download</button>
                                <a href={publicUrl} target="_blank" rel="noreferrer" className="px-4 py-2.5 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-colors">Preview</a>
                            </div>
                        </div>
                    </div>
                </div>
            ) : activeTab === 'batches' ? (
                /* Stock Batches Tab */
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-slate-100 bg-amber-50/50">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">📦</span>
                            <div>
                                <Text variant="h3">Stock Batches</Text>
                                <Text variant="small" color="slate" className="mt-0.5">All procurement batches — buy price, MRP &amp; remaining stock</Text>
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-5 py-4 text-left"><Text variant="label" color="slate">Batch #</Text></th>
                                    <th className="px-5 py-4 text-right"><Text variant="label" color="slate">Purchased</Text></th>
                                    <th className="px-5 py-4 text-right"><Text variant="label" color="slate">Remaining</Text></th>
                                    <th className="px-5 py-4 text-right"><Text variant="label" color="slate">Buy Price</Text></th>
                                    <th className="px-5 py-4 text-right"><Text variant="label" color="slate">MRP</Text></th>
                                    <th className="px-5 py-4 text-center"><Text variant="label" color="slate">Tax</Text></th>
                                    <th className="px-5 py-4 text-right"><Text variant="label" color="slate">Purchased On</Text></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {(product.batches || []).map((b: any) => (
                                    <tr key={b.id} className="hover:bg-amber-50/30 transition-colors">
                                        <td className="px-5 py-4">
                                            <Text variant="mono" weight="bold">{b.batch_number || 'GLOBAL'}</Text>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <Text weight="semibold" color="secondary">{b.purchased_quantity ?? b.stock_quantity}</Text>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <span className={`font-black text-sm ${(b.remaining_quantity ?? b.stock_quantity) > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                {b.remaining_quantity ?? b.stock_quantity}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <Text variant="mono" weight="bold" color="indigo">₹{parseFloat(b.unit_cost ?? b.mrp ?? 0).toLocaleString()}</Text>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <Text variant="mono" color="secondary">₹{parseFloat(b.mrp ?? 0).toLocaleString()}</Text>
                                        </td>
                                        <td className="px-5 py-4 text-center">
                                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[10px] font-bold border border-emerald-100">
                                                {b.tax_rate_name || `${b.tax_rate || 0}%`}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <Text variant="small" color="slate">{b.purchase_date ? new Date(b.purchase_date).toLocaleDateString('en-IN') : '—'}</Text>
                                        </td>
                                    </tr>
                                ))}
                                {(!product.batches || product.batches.length === 0) && (
                                    <tr><td colSpan={7} className="px-6 py-16 text-center"><Text variant="desc" italic>No batches found. Create a purchase to generate stock batches.</Text></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : activeTab === 'units' ? (
                /* Units Tab */
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                        <div>
                            <Text variant="h3">Tracked Units</Text>
                            <Text variant="small" color="slate" className="mt-1">Manage individual serialized items and view their status</Text>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4"><Text variant="label" color="slate">Primary ID</Text></th>
                                    <th className="px-6 py-4"><Text variant="label" color="slate">Status</Text></th>
                                    <th className="px-6 py-4"><Text variant="label" color="slate">Added On</Text></th>
                                    <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Action</Text></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {units.map((u: any) => (
                                    <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <Text variant="mono" weight="bold">{u.primary_identifier || 'N/A'}</Text>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${
                                                u.status === 'available' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 
                                                u.status === 'sold' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                                u.status === 'reserved' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                'bg-slate-50 text-slate-700 border-slate-200'
                                            }`}>
                                                {u.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Text variant="small" color="secondary">{new Date(u.created_at).toLocaleDateString()}</Text>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Link to={`/product_units/${u.id}`} className="text-indigo-600 hover:text-indigo-800 font-semibold text-sm">
                                                View details
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                                {units.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-16 text-center">
                                            <Text variant="desc" italic>No units registered yet.</Text>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-slate-50">
                            <button 
                                onClick={() => fetchUnits(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-100 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
                            >
                                Previous
                            </button>
                            <Text variant="small" color="slate">Page {currentPage} of {totalPages}</Text>
                            <button 
                                onClick={() => fetchUnits(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-100 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                /* History Tab */
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="p-5 border-b border-slate-100 bg-slate-50">
                        <Text variant="h3">Stock History</Text>
                        <Text variant="small" color="slate" className="mt-0.5">All stock movements — purchases, orders &amp; manual adjustments</Text>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-white border-b border-slate-100">
                                <tr>
                                    <th className="px-5 py-4"><Text variant="label" color="slate">Date &amp; Time</Text></th>
                                    <th className="px-5 py-4"><Text variant="label" color="slate">Source</Text></th>
                                    <th className="px-5 py-4"><Text variant="label" color="slate">Reference</Text></th>
                                    <th className="px-5 py-4 text-right"><Text variant="label" color="slate">Qty</Text></th>
                                    <th className="px-5 py-4"><Text variant="label" color="slate">Location</Text></th>
                                    <th className="px-5 py-4"><Text variant="label" color="slate">By</Text></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {adjustments.map((adj: any, idx: number) => {
                                    const isIn = adj.adjustment_type === 'stock_in';
                                    const isMovement = adj.event_type === 'movement';
                                    const date = new Date(adj.adjustment_date);
                                    return (
                                        <tr key={`${adj.event_type}-${adj.id}-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="font-semibold text-slate-800 text-xs">{date.toLocaleDateString('en-IN')}</div>
                                                <div className="text-slate-400 text-[10px] font-mono">{date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                {isMovement ? (
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                                                        adj.reference_type === 'Purchase'
                                                            ? 'bg-blue-50 text-blue-700 border-blue-100'
                                                            : adj.reference_type === 'Order'
                                                            ? 'bg-purple-50 text-purple-700 border-purple-100'
                                                            : 'bg-slate-50 text-slate-600 border-slate-200'
                                                    }`}>
                                                        {adj.reference_type === 'Purchase' ? '🛒' : adj.reference_type === 'Order' ? '📦' : '⚙️'}
                                                        {adj.reference_type || 'System'}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-100">
                                                        ✏️ Manual
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <div className="font-mono text-xs font-bold text-slate-700">{adj.reference_label || adj.reason || '—'}</div>
                                                {adj.notes && adj.notes !== adj.reference_label && (
                                                    <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[160px]" title={adj.notes}>{adj.notes}</div>
                                                )}
                                            </td>
                                            <td className="px-5 py-3.5 text-right">
                                                <span className={`font-black text-sm ${isIn ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                    {isIn ? '+' : '-'}{Math.abs(adj.quantity)}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <Text variant="small" color="slate">{adj.location || '—'}</Text>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <Text variant="small" weight="semibold" color="secondary">{adj.staff_name || 'System'}</Text>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {adjustments.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-16 text-center">
                                            <div className="text-3xl mb-3">📋</div>
                                            <Text variant="desc" italic>No stock history yet. Create a purchase to see movements here.</Text>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showAdjustmentModal && (
                <StockAdjustmentModal 
                  product={product} 
                  onClose={() => setShowAdjustmentModal(false)}
                  onSuccess={() => { loadProduct(); fetchAdjustments(); }}
                />
            )}

            {/* Add Units Modal */}
            {showAddUnitModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full h-[85vh] flex flex-col overflow-hidden animate-in zoom-in duration-300">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center">
                            <div>
                                <Text variant="h2" color="primary">Add Units</Text>
                                <Text variant="desc" className="mt-1">Register new serial numbers/units for tracking</Text>
                            </div>
                            <button onClick={() => setShowAddUnitModal(false)} className="p-3 hover:bg-slate-50 rounded-2xl text-slate-400 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-8">
                            <div className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-4 mb-8 flex gap-4 items-center">
                                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shrink-0">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <Text variant="small" color="indigo" weight="medium">
                                    <Text as="span" weight="bold">Pro-tip:</Text> You can paste multiple rows from Excel or Google Sheets directly into the first column.
                                </Text>
                            </div>

                            <div className="border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="py-4 px-6 text-left"><Text variant="label" color="slate">#</Text></th>
                                            {(product?.tracking_types?.length ? product.tracking_types : ['Serial Number']).map((t: string, idx: number) => (
                                                <th key={idx} className="py-4 px-6 text-left"><Text variant="label" color="slate">{t}</Text></th>
                                            ))}
                                            <th className="py-4 px-6 text-left"><Text variant="label" color="slate">Batch</Text></th>
                                            <th className="py-4 px-6 text-right"><Text variant="label" color="slate">Action</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {unitRows.map((row, index) => {
                                            const types = product?.tracking_types?.length ? product.tracking_types : ['Serial Number'];
                                            return (
                                                <tr key={index} className="hover:bg-slate-50/30">
                                                    <td className="py-4 px-6"><Text variant="mono" color="slate" className="text-xs">{index + 1}</Text></td>
                                                    {types.map((t: string, typeIdx: number) => (
                                                        <td key={typeIdx} className="py-4 px-6">
                                                            <input
                                                                type="text"
                                                                value={row[t] || ''}
                                                                onChange={(e) => updateRow(index, t, e.target.value)}
                                                                onPaste={typeIdx === 0 ? (e) => handlePaste(e, index) : undefined}
                                                                placeholder={`Enter ${t}`}
                                                                className="w-full bg-transparent border-b border-slate-200 focus:border-indigo-500 focus:ring-0 p-1 text-sm font-bold text-slate-800 uppercase placeholder-slate-300 font-mono transition-all"
                                                            />
                                                        </td>
                                                    ))}
                                                    <td className="py-4 px-6">
                                                        <input
                                                            type="text"
                                                            value={row.batch_number || ''}
                                                            onChange={(e) => updateRow(index, 'batch_number', e.target.value)}
                                                            placeholder="Batch #"
                                                            className="w-full bg-transparent border-b border-slate-200 focus:border-indigo-500 focus:ring-0 p-1 text-sm text-slate-600 uppercase placeholder-slate-300 font-mono transition-all"
                                                        />
                                                    </td>
                                                    <td className="py-4 px-6 text-right">
                                                        <button onClick={() => removeRow(index)} className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <button onClick={addEmptyRow} className="mt-6 px-6 py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-500 hover:text-indigo-600 hover:border-indigo-200 transition-all text-sm font-bold w-full bg-slate-50/50">+ Add Another Row</button>
                        </div>

                        <div className="p-8 border-t border-slate-100 bg-slate-50 flex justify-end gap-4">
                            <button onClick={() => setShowAddUnitModal(false)} className="px-8 py-3 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                            <button onClick={handleSaveUnits} className="px-12 py-3 bg-indigo-600 text-white rounded-2xl text-sm font-bold shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all hover:-translate-y-0.5">Register Units</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductShow;
